export const TEAM_PROPERTY = 'team';
export const SUPPORT_GROUP_PROPERTY = 'supportgroup';
export const ASSIGNEE_PROPERTY = 'assignee';

interface TeamsLinked {
  Id: number;
  TeamsAadObjectId?: string;
  TeamsChannelId?: string;
}

interface GroupWithMembers extends TeamsLinked {
  Members?: { Id: number }[];
}

type DoRow = { Property?: string; Value?: string };
type RowChange = { index: number; value: string };

const sameTeamsTeam = (a: TeamsLinked, b: TeamsLinked) =>
  !!a.TeamsAadObjectId && a.TeamsAadObjectId.toLowerCase() === b.TeamsAadObjectId?.toLowerCase();

/**
 * The first of `candidates` in the same Teams team as `source`. A team and a group are linked only by sharing a
 * Teams team, and one Teams team can back several of either (one per channel), so the one on `source`'s own channel
 * is preferred: for a team that is its default group, which is what the server falls back to (#31645).
 */
export const findLinked = <T extends TeamsLinked>(source: TeamsLinked, candidates: T[]): T | undefined => {
  const linked = candidates.filter(c => sameTeamsTeam(c, source));
  return linked.find(c => c.TeamsChannelId === source.TeamsChannelId) ?? linked[0];
};

export const isTeamOrGroupProperty = (property?: string) =>
  [TEAM_PROPERTY, SUPPORT_GROUP_PROPERTY].includes(property?.toLowerCase());

/**
 * The value a Team or Group row should start with when the other of the two is already set on the action: the team
 * that group belongs to, or that team's group. Undefined when the row already has a value, the other row is not set,
 * or nothing matches, so a choice the user made is never overwritten.
 */
export const getTeamGroupDefaultValue = (
  rows: { Property?: string; Value?: string }[],
  rowIndex: number,
  teams: TeamsLinked[],
  groups: TeamsLinked[]
): string | undefined => {
  const row = rows[rowIndex];
  if (!row || row.Value || !isTeamOrGroupProperty(row.Property)) return undefined;

  const isTeamRow = row.Property.toLowerCase() === TEAM_PROPERTY;
  const otherProperty = isTeamRow ? SUPPORT_GROUP_PROPERTY : TEAM_PROPERTY;
  const otherValue = rows.find((r, i) => i !== rowIndex && r.Property?.toLowerCase() === otherProperty)?.Value;
  if (!otherValue) return undefined;

  const source = (isTeamRow ? groups : teams).find(x => String(x.Id) === otherValue);
  const match = source && findLinked(source, isTeamRow ? teams : groups);
  return match ? String(match.Id) : undefined;
};

const findRowIndex = (rows: { Property?: string }[], property: string) =>
  rows.findIndex(r => r.Property?.toLowerCase() === property);

/**
 * The ids the Group row may offer: the groups of the team the Team row sets. Undefined - no restriction - when there
 * is no Team row, it has no value yet, or its team is unknown.
 */
export const getSelectableGroupIds = (
  rows: { Property?: string; Value?: string }[],
  teams: TeamsLinked[],
  groups: TeamsLinked[]
): number[] | undefined => {
  const teamValue = rows[findRowIndex(rows, TEAM_PROPERTY)]?.Value;
  const team = teamValue ? teams.find(t => String(t.Id) === teamValue) : undefined;
  return team ? groups.filter(g => sameTeamsTeam(g, team)).map(g => g.Id) : undefined;
};

/**
 * Where the Group row moves when the Team row changes to `teamValue`: the new team's default group, or empty when the
 * team has no group. Undefined when there is no Group row or the team is unknown.
 */
export const getGroupRowForTeamChange = (
  rows: { Property?: string; Value?: string }[],
  teamValue: string,
  teams: TeamsLinked[],
  groups: TeamsLinked[]
): { index: number; value: string } | undefined => {
  const index = findRowIndex(rows, SUPPORT_GROUP_PROPERTY);
  const team = teams.find(t => String(t.Id) === teamValue);
  if (index < 0 || !team) return undefined;

  const group = findLinked(team, groups);
  return { index, value: group ? String(group.Id) : '' };
};

/**
 * The user ids the Assignee row may offer: the members of the group the Group row sets, or with no group, the members
 * of every group of the team the Team row sets. Undefined - no restriction - when neither is set, or a group's members
 * were not loaded.
 */
export const getSelectableAssigneeIds = (
  rows: DoRow[],
  teams: TeamsLinked[],
  groups: GroupWithMembers[]
): number[] | undefined => {
  const memberIds = (from: GroupWithMembers[]) =>
    from.every(g => Array.isArray(g.Members)) ? [...new Set(from.flatMap(g => g.Members.map(m => m.Id)))] : undefined;

  const groupValue = rows[findRowIndex(rows, SUPPORT_GROUP_PROPERTY)]?.Value;
  const group = groupValue ? groups.find(g => String(g.Id) === groupValue) : undefined;
  if (group) return memberIds([group]);

  const teamValue = rows[findRowIndex(rows, TEAM_PROPERTY)]?.Value;
  const team = teamValue ? teams.find(t => String(t.Id) === teamValue) : undefined;
  return team ? memberIds(groups.filter(g => sameTeamsTeam(g, team))) : undefined;
};

/**
 * Everything that follows from the Team or Group row at `changedIndex` being set, in order: an empty row takes its
 * default from the other one, a new team moves the Group row to that team's default group, and then an assignee who is
 * no longer selectable is cleared. Only a user id is ever cleared - "Round Robin" is not a user and picks from whatever
 * group the ticket ends up in.
 */
export const getTeamGroupCascade = (
  rows: DoRow[],
  changedIndex: number,
  teams: TeamsLinked[],
  groups: GroupWithMembers[]
): RowChange[] => {
  const working = rows.map(r => ({ ...r }));
  const changes: RowChange[] = [];
  const set = ({ index, value }: RowChange) => {
    working[index].Value = value;
    changes.push({ index, value });
  };

  const row = working[changedIndex];
  if (!row || !isTeamOrGroupProperty(row.Property)) return changes;

  if (!row.Value) {
    const value = getTeamGroupDefaultValue(working, changedIndex, teams, groups);
    if (value === undefined) return changes;
    set({ index: changedIndex, value });
  } else if (row.Property.toLowerCase() === TEAM_PROPERTY) {
    const groupChange = getGroupRowForTeamChange(working, row.Value, teams, groups);
    if (groupChange && groupChange.value !== working[groupChange.index].Value) set(groupChange);
  }

  const assigneeIndex = findRowIndex(working, ASSIGNEE_PROPERTY);
  const assigneeValue = working[assigneeIndex]?.Value;
  const selectable = getSelectableAssigneeIds(working, teams, groups);
  if (selectable && /^\d+$/.test(assigneeValue ?? '') && !selectable.includes(Number(assigneeValue)))
    set({ index: assigneeIndex, value: '' });

  return changes;
};
